import React, { memo, useCallback, useEffect, useRef } from 'react';
import { css, useTheme } from '@emotion/react';
import { Icon } from '../../../Icon/Icon';
import { globals } from '../../../../utils/globals';
import AlgoliaSearch from '../../../Search/AlgoliaSearch';
import Grid from '../../../grid/Grid';
import { useScreenLayout } from '../../../../hooks/useScreenLayout';
import { useUIConfigStore } from 'common-state';

export type SearchBarProps = {
  isSticky?: boolean;
};

export const HeaderSearchBar = memo((props: SearchBarProps) => {
  const { isSticky } = props;
  const stateUpdate = useUIConfigStore(state => state.selectedState);
  const searchBarRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();
  const wrapper = css`
    ${theme.widgets.GlobalHeader?.headerSearchInput}
  `;
  const { screen } = useScreenLayout();

  const getSearchSuggestionPos = useCallback(() => {
    const searchBar = searchBarRef.current;
    const desktopView = ['lg', 'xl', 'xxl'].includes(screen);
    if (!searchBar) {
      return 0;
    }
    const searchbarContainer = searchBar.closest('#search-nav-bar') as HTMLElement;
    const searchbarGridContainer = searchBar.querySelector('.searchbar-grid-block-container') as HTMLElement;
    let bottom;
    if (desktopView) {
      bottom = Math.ceil(searchbarContainer?.getBoundingClientRect().bottom);
    } else {
      bottom = Math.ceil(searchbarGridContainer?.getBoundingClientRect().bottom);
    }
    return bottom;
  }, [screen]);

  const searchPositionRef = useRef(getSearchSuggestionPos);
  searchPositionRef.current = getSearchSuggestionPos;

  const setSearchOverLay = useCallback(() => {
    const top = searchPositionRef.current();
    const searchOverlay = searchBarRef.current?.querySelector('.search-overlay') as HTMLElement;
    searchOverlay?.style?.setProperty('top', `${top}px`);
    return top;
  }, []);

  const toggleSearchFocus = useCallback(
    (focus: boolean) => {
      const searchBar = searchBarRef.current;
      if (!searchBar) {
        return;
      }
      const navbarCenterElem = searchBar.closest('.navbar-center-container');
      const navbarLeftElem = navbarCenterElem?.parentElement?.querySelector('.navbar-left-container');
      const newHeaderReactAppElem = document.querySelector('#newHeaderReactApp');
      const mainNavBars = document.querySelectorAll('.sticky-nav-bar .widget-MainNavBar');

      const clsInputFocus = 'input-on-focus';
      const clsSearchOverlay = 'active-search-overlay';
      const clsMainNavSearchFocus = 'searchbar-focused';
      if (focus) {
        navbarCenterElem?.classList.add(clsInputFocus);
        navbarLeftElem?.classList.add(clsInputFocus);
        newHeaderReactAppElem?.classList.add(clsSearchOverlay);
        mainNavBars.forEach(mainNavBar => mainNavBar.classList.add(clsMainNavSearchFocus));
        document.getElementsByTagName('html')[0].style.overflow = 'hidden';
        setSearchOverLay();
      } else {
        navbarCenterElem?.classList.remove(clsInputFocus);
        navbarLeftElem?.classList.remove(clsInputFocus);
        newHeaderReactAppElem?.classList.remove(clsSearchOverlay);
        mainNavBars.forEach(mainNavBar => mainNavBar.classList.remove(clsMainNavSearchFocus));
        document.getElementsByTagName('html')[0].style.overflow = 'visible';
      }
    },
    [setSearchOverLay],
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        toggleSearchFocus(false);
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [toggleSearchFocus]);

  useEffect(() => {
    const searchBox = searchBarRef.current?.querySelector('.aa-Input');
    const listenFocus = () => toggleSearchFocus(true);
    searchBox?.addEventListener('focus', listenFocus);
    return () => searchBox?.removeEventListener('focus', listenFocus);
  }, [stateUpdate, toggleSearchFocus]);

  const cancelSearch = () => toggleSearchFocus(false);

  const handleSearchBarBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (
      !event.relatedTarget ||
      (event.relatedTarget instanceof Node && searchBarRef.current?.contains(event.relatedTarget))
    ) {
      return;
    }
    cancelSearch();
  };

  const mobileSearch = () => {
    toggleSearchFocus(true);
    const searchForm = searchBarRef.current?.querySelector('.aa-Form') as HTMLFormElement;
    searchForm?.reset();
  };

  return (
    <div ref={searchBarRef} css={wrapper} className="header-search-bar" onBlurCapture={handleSearchBarBlur}>
      <span
        data-testid="search-icon"
        className="search-icon"
        onClick={mobileSearch}
        onKeyDown={e => e.key === 'Enter' && mobileSearch()}
        tabIndex={0}
        role="button"
      >
        <Icon kind="search" size="m" />
      </span>
      <div className="searchbar-grid-block-container">
        {globals?.algoliaIndexName ? (
          <AlgoliaSearch isSticky={isSticky} onCancelSearch={cancelSearch} setSearchOverLay={setSearchOverLay} />
        ) : (
          // * The Below code is a temporary fix for algolia issue will be removed in future
          <Grid align="center" className="searchbar-grid">
            <Grid.Col className="w-100">
              <div className="searchbar-input-container">
                <input
                  type="search"
                  placeholder="Search for products"
                  name="search"
                  className="temp"
                  autoComplete="off"
                />
                <span className="prefix-icon">
                  <Icon kind="search" size="s" />
                </span>
              </div>
            </Grid.Col>
          </Grid>
        )}
      </div>
    </div>
  );
});
